import {useUpmPlant} from '@hconnect/upm'
import {clx, Button} from '@hm/fundamental'
import {mdiWindowClose, mdiCheck} from '@mdi/js'
import {useFormContext, type UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {AddKilnShutdownConfirmationModal} from './AddKilnShutdownConfirmationModal'

import {useRefraModal, useKilns, useShutdowns} from '@refra/modules/common/providers'
import type {ShutdownForm} from '@refra/modules/common/types'
import {useAddShutdown} from '@refra/modules/refra-planning/hooks/mutations'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface AddKilnShutdownActionsProps {
  isRowLayout?: boolean
}
export const AddKilnShutdownActions: React.FC<AddKilnShutdownActionsProps> = ({isRowLayout}) => {
  const {t} = useTranslation()
  const {openModal} = useRefraModal()

  const plantCode = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {selectedKiln} = useKilns()

  const {setNewShutdown} = useShutdowns()

  const {mutate: addShutdown, isPending: isAddingInProgress} = useAddShutdown({
    onSuccessCallback: () => setNewShutdown(null)
  })

  const {
    formState: {isDirty, isValid},
    handleSubmit
  }: UseFormReturn<ShutdownForm> = useFormContext()

  const onCancelKilnShutdownAddition = () => setNewShutdown(null)

  const onAddKilnShutdown = handleSubmit((kilnShutdownForm) => {
    if (!isDirty || !isValid) return

    const {shutdownType, name, interventionDate, feedingDate} = kilnShutdownForm

    openModal(
      <AddKilnShutdownConfirmationModal
        onCloseCallback={onCancelKilnShutdownAddition}
        onConfirmCallback={() =>
          addShutdown({
            plantUpmId: upmPlant.upmId,
            kilnUpmId: selectedKiln.upmId,
            shutdownDTO: {name, shutdownType, interventionDate, feedingDate}
          })
        }
      />
    )
  })

  return (
    <div className={clx('flex gap-3', !isRowLayout && 'justify-end w-full')}>
      <Button
        icon={mdiWindowClose}
        disabled={isAddingInProgress}
        variant="secondary"
        onClick={onCancelKilnShutdownAddition}
        data-cy="add_kiln_shutdown_actions_cancel_button"
      >
        {t('common.cancel')}
      </Button>
      <Button
        icon={mdiCheck}
        disabled={isAddingInProgress}
        onClick={onAddKilnShutdown}
        data-cy="add_kiln_shutdown_actions_done_button"
      >
        {t('common.done')}
      </Button>
    </div>
  )
}
